import { Button, IconButton, Stack, Typography } from '@mui/material';
import {
  PaymentFrequency,
  Product,
  ProductBillingType,
  ProductType,
  SchoolYear,
  useGetSchoolPaymentFrequencies,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { CopyIcon, Counter, DropdownYears, EditIcon, Loading, PlusIcon } from '@schooly/style';
import { isDateInPast } from '@schooly/utils/date';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import useSchoolYears from '../../../../hooks/useSchoolYears';
import { EmptyTypes } from '../EmptyTypes';
import {
  getCurrencySymbol,
  getCurrentProductTypes,
  getEmptyProductTypesMessage,
  getProductTypeYearId,
  getSortedFrequencies,
  hasFrequenciesByType,
  useProductFrequency,
  useSchoolYearsInProduct,
} from '../helpers';
import { AllFrequenciesNotInUse, ProductTypeLayout } from '../SchoolProductCommon';
import { AccountSeparator } from '../SchoolProductCreateModal/AccountSelect';
import { PreviewVariantsTable } from './PreviewVariantsTable';
import { OnEditVariantsParams } from './SchoolProductModal';

export type SchoolProductModalVariantsProps = {
  product: Product;
  schoolId: string;
  onEdit?: (params: OnEditVariantsParams) => void;
};

export const SchoolProductModalVariants: FC<SchoolProductModalVariantsProps> = ({
  product,
  schoolId,
  onEdit,
}) => {
  const { $t } = useIntl();
  const { schoolYears } = useSchoolYears();
  // TR-6100 Invoices. Frequencies(Cut) To be used later
  // const [showAllFrequencies, setShowAllFrequencies] = useState(false);
  const { permissions } = useAuth();

  const { types: allTypes, type } = product;
  const isRecurring = type === ProductBillingType.Recurring;

  const canShowYearInSelect = useCallback(
    (year?: SchoolYear) =>
      Boolean(
        year &&
          (!isDateInPast(year.end) ||
            allTypes.some((t) => getProductTypeYearId(t, schoolYears) === year.id)),
      ),
    [allTypes, schoolYears],
  );

  const { selectedYear, defaultYear, setSelectedYear, yearsForSelect, previousYear } =
    useSchoolYearsInProduct(canShowYearInSelect, !isRecurring);
  const selectedYearId = selectedYear?.id ?? '';
  const copyFromYearId = previousYear?.id ?? '';

  const { data, isLoading } = useGetSchoolPaymentFrequencies(
    { school_id: schoolId, year_id: selectedYearId },
    { enabled: !!selectedYearId },
  );

  const frequencies = useMemo(() => {
    return data?.frequencies
      ? getSortedFrequencies(data.frequencies).filter((f) => !!f.in_use)
      : [];
    // TR-6100 Invoices. Frequencies(Cut) To be used later
    // return showAllFrequencies ? frequencies : frequencies?.filter((f) => f.in_use);
  }, [data]);

  const canViewFrequency =
    permissions.includes('product_and_invoice_viewer') ||
    permissions.includes('registration_manager');

  const { allTypesNotActive, productTypeNotActiveMap } = useProductFrequency({
    frequencies,
    product,
  });

  const types = useMemo(
    () =>
      getCurrentProductTypes({
        types: allTypes,
        productType: type,
        schoolYears,
        yearId: selectedYearId,
      }),
    [allTypes, type, schoolYears, selectedYearId],
  );
  const hasCurrentTypes = Boolean(types.length);
  const canEdit = !!onEdit && hasCurrentTypes;

  const { hasActiveFrequencies, hasFrequencies } = useMemo(
    () => ({
      hasFrequencies: hasFrequenciesByType(data?.frequencies ?? [], type),
      hasActiveFrequencies: hasFrequenciesByType(frequencies, type),
    }),
    [data?.frequencies, frequencies, type],
  );

  const renderContent = () => {
    if (hasCurrentTypes) {
      return types.map((t) => (
        <ProductTypeVariants
          schoolId={schoolId}
          type={t}
          key={t.id}
          frequencies={frequencies}
          canViewFrequency={canViewFrequency}
          notActive={allTypesNotActive ? false : productTypeNotActiveMap[t.id]}
        />
      ));
    }

    if (isLoading) {
      return <Loading />;
    }

    const canCopy = Boolean(
      copyFromYearId &&
        getCurrentProductTypes({
          types: allTypes,
          productType: type,
          schoolYears,
          yearId: copyFromYearId,
        }).length,
    );

    const copyButton =
      canCopy && onEdit ? (
        <Button
          startIcon={<CopyIcon />}
          onClick={() => onEdit(isRecurring ? { yearId: selectedYearId, copyFromYearId } : {})}
        >
          {$t({ id: 'products-CopyFromPreviousYear' })}
        </Button>
      ) : null;

    const setButton = onEdit ? (
      <Button
        variant="outlined"
        startIcon={<EditIcon />}
        onClick={() => onEdit(isRecurring ? { yearId: selectedYearId } : {})}
      >
        {$t({ id: 'products-SetFromScratch' })}
      </Button>
    ) : null;

    const setFrequenciesButton =
      //currently user cannot add frequencies, so we only show button for editing existing ones
      hasFrequencies && canViewFrequency ? (
        <Link to={`/settings/frequencies/year/${selectedYearId}`} target="_blank">
          <Button startIcon={<PlusIcon />}>{$t({ id: 'frequencies-SetFrequencies' })}</Button>
        </Link>
      ) : null;

    return (
      <EmptyTypes
        message={$t({ id: getEmptyProductTypesMessage(hasFrequencies, hasActiveFrequencies) })}
        actions={
          hasActiveFrequencies ? (
            <Stack direction="row" gap={1}>
              {copyButton}
              {setButton}
            </Stack>
          ) : (
            setFrequenciesButton
          )
        }
      />
    );
  };

  return (
    <>
      <Stack justifyContent="space-between" flexDirection="row" alignItems="center" mb={2.25}>
        <Stack flexDirection="row" alignItems="center">
          <Typography variant="h2">
            {$t({ id: 'products-Types' }, { productName: product.name })}
          </Typography>
          {hasCurrentTypes && (
            <Stack direction="row" gap={1}>
              <Counter sx={{ minWidth: 20, minHeight: 20, textAlign: 'center' }}>
                {types.length}
              </Counter>
              {allTypesNotActive && (
                <AllFrequenciesNotInUse name={$t({ id: 'products-Product' })} />
              )}
            </Stack>
          )}
        </Stack>
        <Stack gap={2.5} direction="row" alignItems="baseline">
          {/* TR-6100 Invoices. Frequencies(Cut) To be used later */}
          {/* {hasCurrentTypes && (
            <FormControlLabel
              sx={{ ml: 0, mt: 1 }}
              control={
                <Switch
                  checked={showAllFrequencies}
                  onChange={() => setShowAllFrequencies((v) => !v)}
                />
              }
              label={$t({ id: 'frequencies-ShowDisabledFrequencies' })}
            />
          )} */}
          {!!selectedYear && isRecurring && (
            <DropdownYears
              years={yearsForSelect}
              defaultYear={defaultYear}
              currentYear={selectedYear}
              onYearChange={setSelectedYear}
            />
          )}
          {canEdit && (
            <IconButton
              inverse
              onClick={() => onEdit(isRecurring ? { yearId: selectedYearId } : {})}
              sx={{ color: 'common.grey2' }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Stack>
      </Stack>
      {renderContent()}
    </>
  );
};

type ProductTypeVariantsProps = {
  schoolId: string;
  type: ProductType;
  frequencies: PaymentFrequency[];
  canViewFrequency: boolean;
  notActive: boolean;
};

const ProductTypeVariants: FC<ProductTypeVariantsProps> = ({
  type,
  schoolId,
  frequencies,
  canViewFrequency,
  notActive,
}) => {
  const { variants, billing_connection } = type;

  const currencySymbol = getCurrencySymbol(billing_connection.legal_entity_currency);

  const variantsFrequencies = useMemo(
    () =>
      frequencies
        .filter((fr) => variants.some((v) => v.prices.some((p) => p.frequency_id === fr?.id)))
        .filter(Boolean),
    [frequencies, variants],
  );

  const { $t } = useIntl();

  return (
    <Stack>
      <ProductTypeLayout>
        {(type.name || notActive) && (
          <Stack
            direction="row"
            alignItems="center"
            width={type.name ? 472 : undefined}
            pr={1}
            gap={1}
          >
            {type.name && <Typography variant="h2">{type.name}</Typography>}

            {notActive && (
              <AllFrequenciesNotInUse name={`${$t({ id: 'products-Type' })} ${type.name}`} />
            )}
          </Stack>
        )}
        <Stack alignSelf="center" direction="row" gap={1}>
          <Typography variant="h3"> {billing_connection.legal_entity_display_name}</Typography>
          {billing_connection.legal_entity_account.type === 'xero' && (
            <Stack direction="row" alignItems="baseline">
              <AccountSeparator selected />
              <Typography variant="h3">{billing_connection.legal_entity_account.name}</Typography>
            </Stack>
          )}
        </Stack>
      </ProductTypeLayout>

      <Stack
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          borderBottomLeftRadius: theme.spacing(1),
          borderBottomRightRadius: theme.spacing(1),
          mb: theme.spacing(2.5),
          overflow: 'auto',
        })}
      >
        <PreviewVariantsTable
          addedFrequencies={variantsFrequencies}
          canViewFrequency={canViewFrequency}
          schoolId={schoolId}
          variants={variants}
          currencySymbol={currencySymbol}
        />
      </Stack>
    </Stack>
  );
};
